import { z } from "zod";

export const flashcardTextSchema = z
  .string()
  .trim()
  .min(1, "Enter text for this side of the flashcard.")
  .max(10_000, "Keep each side of the flashcard under 10,000 characters.");

export const flashcardNotesSchema = z.string().default("");

export const flashcardCollectionNameSchema = z
  .string()
  .trim()
  .min(1, "Enter a collection name.")
  .max(100, "Keep the collection name under 100 characters.")
  .transform((value) => value.normalize("NFKC"));

export const createFlashcardsInputSchema = z
  .object({
    question: flashcardTextSchema,
    answer: flashcardTextSchema,
    notes: flashcardNotesSchema,
    collectionId: z.uuid().nullable(),
    createReverse: z.boolean(),
    idempotencyKey: z.uuid(),
  })
  .strict();

export const createFlashcardCollectionInputSchema = z
  .object({ name: flashcardCollectionNameSchema })
  .strict();

export const activityFlashcardParamsSchema = z
  .object({
    activityId: z.uuid(),
    flashcardId: z.uuid(),
  })
  .strict();

export const flashcardCollectionSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
  })
  .strict();

export const flashcardSchema = z
  .object({
    id: z.uuid(),
    activityId: z.uuid().nullable(),
    collectionId: z.uuid().nullable(),
    question: z.string(),
    answer: z.string(),
    notes: z.string(),
    reviewDate: z.iso.date().nullable(),
    archivedAt: z.string().nullable(),
    reviewVersion: z.number().int().positive(),
    createdAt: z.string(),
  })
  .strict();

export const flashcardReviewOutcomeSchema = z.enum([
  "keep-current",
  "one-week",
  "one-month",
  "three-months",
  "archive",
]);

export const flashcardReviewSummarySchema = z
  .object({
    ready: z.number().int().nonnegative(),
    scheduled: z.number().int().nonnegative(),
    archived: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
  })
  .strict();

export const flashcardReviewCollectionSchema = flashcardReviewSummarySchema.extend({
  id: z.uuid().nullable(),
  name: z.string(),
});

export const flashcardReviewCardSchema = z
  .object({
    id: z.uuid(),
    collectionId: z.uuid().nullable(),
    question: z.string(),
    answer: z.string(),
    notes: z.string(),
    reviewVersion: z.number().int().positive(),
  })
  .strict();

export const flashcardReviewOverviewSchema = z
  .object({
    summary: flashcardReviewSummarySchema,
    collections: z.array(flashcardReviewCollectionSchema),
    readyCards: z.array(flashcardReviewCardSchema),
  })
  .strict();

export const reviewFlashcardParamsSchema = z.object({ flashcardId: z.uuid() }).strict();

export const manageFlashcardsQuerySchema = z
  .object({
    collectionId: z.union([z.uuid(), z.literal("uncollected")]),
    page: z.coerce.number().int().positive().default(1),
  })
  .strict();

export const searchFlashcardsQuerySchema = z
  .object({
    search: z.string().trim().min(1).max(120),
    page: z.coerce.number().int().positive().default(1),
  })
  .strict();

export const manageFlashcardParamsSchema = z.object({ flashcardId: z.uuid() }).strict();

export const updateFlashcardInputSchema = z
  .object({
    question: flashcardTextSchema,
    answer: flashcardTextSchema,
    notes: flashcardNotesSchema,
    collectionId: z.uuid().nullable(),
    expectedVersion: z.number().int().positive(),
  })
  .strict();

export const deleteManagedFlashcardInputSchema = z
  .object({ expectedVersion: z.number().int().positive() })
  .strict();

export const managedFlashcardStatusActionSchema = z.enum(["archive", "unarchive", "bring-current"]);

export const updateManagedFlashcardStatusInputSchema = z
  .object({
    action: managedFlashcardStatusActionSchema,
    expectedVersion: z.number().int().positive(),
  })
  .strict();

export const managedFlashcardsResponseSchema = z
  .object({
    flashcards: z.array(flashcardSchema),
    today: z.iso.date(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
    pageCount: z.number().int().positive(),
  })
  .strict();

export const updateFlashcardResponseSchema = z.object({ flashcard: flashcardSchema }).strict();

export const updateManagedFlashcardStatusResponseSchema = z
  .object({ flashcard: flashcardSchema })
  .strict();

export const deleteManagedFlashcardResponseSchema = z
  .object({ deletedFlashcardId: z.uuid() })
  .strict();

export const reviewFlashcardInputSchema = z
  .object({
    outcome: flashcardReviewOutcomeSchema,
    idempotencyKey: z.uuid(),
    expectedVersion: z.number().int().positive(),
  })
  .strict();

export const reviewFlashcardResponseSchema = z
  .object({
    flashcardId: z.uuid(),
    outcome: flashcardReviewOutcomeSchema,
    reviewDate: z.iso.date().nullable(),
    archivedAt: z.string().nullable(),
    reviewVersion: z.number().int().positive(),
    replayed: z.boolean(),
  })
  .strict();

export const flashcardOptionsResponseSchema = z
  .object({
    collections: z.array(flashcardCollectionSchema),
    flashcardCount: z.number().int().nonnegative(),
  })
  .strict();

export const lessonFlashcardsResponseSchema = z
  .object({
    flashcards: z.array(flashcardSchema),
  })
  .strict();

export const createFlashcardsResponseSchema = z
  .object({
    flashcards: z.array(flashcardSchema).min(1).max(2),
    flashcardCount: z.number().int().nonnegative(),
    replayed: z.boolean(),
  })
  .strict();

export const createFlashcardCollectionResponseSchema = z
  .object({ collection: flashcardCollectionSchema })
  .strict();

export const deleteFlashcardResponseSchema = z
  .object({
    deletedFlashcardId: z.uuid(),
    flashcardCount: z.number().int().nonnegative(),
  })
  .strict();

export type CreateFlashcardsInput = z.infer<typeof createFlashcardsInputSchema>;
export type CreateFlashcardCollectionInput = z.infer<typeof createFlashcardCollectionInputSchema>;
export type Flashcard = z.infer<typeof flashcardSchema>;
export type FlashcardReviewOutcome = z.infer<typeof flashcardReviewOutcomeSchema>;
export type FlashcardReviewSummary = z.infer<typeof flashcardReviewSummarySchema>;
export type FlashcardReviewCollection = z.infer<typeof flashcardReviewCollectionSchema>;
export type FlashcardReviewCard = z.infer<typeof flashcardReviewCardSchema>;
export type FlashcardReviewOverview = z.infer<typeof flashcardReviewOverviewSchema>;
export type ReviewFlashcardInput = z.infer<typeof reviewFlashcardInputSchema>;
export type ReviewFlashcardResponse = z.infer<typeof reviewFlashcardResponseSchema>;
export type FlashcardCollection = z.infer<typeof flashcardCollectionSchema>;
export type FlashcardOptionsResponse = z.infer<typeof flashcardOptionsResponseSchema>;
export type LessonFlashcardsResponse = z.infer<typeof lessonFlashcardsResponseSchema>;
export type CreateFlashcardsResponse = z.infer<typeof createFlashcardsResponseSchema>;
export type CreateFlashcardCollectionResponse = z.infer<
  typeof createFlashcardCollectionResponseSchema
>;
export type DeleteFlashcardResponse = z.infer<typeof deleteFlashcardResponseSchema>;
export type ManageFlashcardsQuery = z.infer<typeof manageFlashcardsQuerySchema>;
export type SearchFlashcardsQuery = z.infer<typeof searchFlashcardsQuerySchema>;
export type ManagedFlashcardsResponse = z.infer<typeof managedFlashcardsResponseSchema>;
export type UpdateFlashcardInput = z.infer<typeof updateFlashcardInputSchema>;
export type UpdateFlashcardResponse = z.infer<typeof updateFlashcardResponseSchema>;
export type DeleteManagedFlashcardInput = z.infer<typeof deleteManagedFlashcardInputSchema>;
export type DeleteManagedFlashcardResponse = z.infer<typeof deleteManagedFlashcardResponseSchema>;
export type ManagedFlashcardStatusAction = z.infer<typeof managedFlashcardStatusActionSchema>;
export type UpdateManagedFlashcardStatusInput = z.infer<
  typeof updateManagedFlashcardStatusInputSchema
>;
export type UpdateManagedFlashcardStatusResponse = z.infer<
  typeof updateManagedFlashcardStatusResponseSchema
>;
